import type { ReactElement, FC } from "react"

import { Box, Divider, Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material"


interface ListProps {
    title?:string, 
    loading?:boolean, 
    items?: ListItemData[]
}

export const List:FC<ListProps> = ({
    title,
    loading,
    items,
}) => {

    const titleContent = (loading && title)?
                        <Skeleton height={48}/>:
                        (title)?
                        <>
                            <Typography 
                                variant="h6" 
                                sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden'
                                }}>
                                    {title}
                            </Typography>
                            <Divider/>
                        </>:''

    const content = items?.map( (item, key) => {
        return <ListItem 
                    key={key}
                    loading={loading}
                    {...item}
                />
    })

    return (
        <Stack spacing={2}>
            {titleContent}
            {content}
        </Stack>
    )
}  

export interface ListItemData {
    title?: string, 
    content?: string | ReactElement | number | null,
    loading?: boolean,
    monospace?: boolean,
    pre?: boolean
}

export const ListItem:FC<ListItemData> = ({
    title, 
    content,
    loading,
    monospace,
    pre
}) => {

    const theme = useTheme()

    let innerContent

    if(loading) {
        innerContent = <Skeleton height={23}/>
    } else {
        if(content) {

            const sx = {
                whiteSpace: 'pre-line', 
                pl: 4,
                ...(monospace && {fontFamily: 'monospace', wordBreak: 'break-word'})
            }
            
            let cnt = (typeof content === 'string' || typeof content === 'number')?
                      (
                        (pre)?(
                             <Typography variant="body1" component='pre' fontFamily='monospace' pl={4}>
                                {content}
                            </Typography>
                        ):(
                            <Typography variant="body1" sx={sx}>
                                {content}
                            </Typography>
                        )
                      ):
                      (
                        <Box pl={4}>
                            {content}
                        </Box>
                      )
    
            innerContent = cnt
        }
    }

    return (
        <Grid container sx={{boxSizing: 'border-box'}}>
            <Grid size={3}>
                <Typography 
                    variant="subtitle2"
                    component='p'
                    title={title}
                    sx={{
                        overflow:'hidden', 
                        whiteSpace:'nowrap', 
                        textOverflow:'ellipsis',
                    }}
                    >
                    {title}
                </Typography>
            </Grid>
            <Grid 
                size={9} 
                sx={{
                    wordBreak: "break-all", 
                    color: theme.palette.text.secondary,
                }}>
                {innerContent}
            </Grid>
        </Grid>
    )
}